import * as React from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom';
import {
  Gear as SettingsIcon,
  Node as ConnectorsIcon,
  Flowchart as IntegrationsIcon,
  Group as CustomersIcon,
  Gears as MetaDataGeneratorIcon,
  Compare as ContentReindexIcon,
  Power as PluginsIcon,
  Dashboard as DashboardIcon,
  LightningBolt as FeaturesIcon,
  Apps as ProductsIcon,
  Translate,
  Megaphone,
} from '@mineral/icons';
import {
  Box,
  SideNavMenu,
  SideNavMenuItem,
  SideNavMenuItemProps,
  NAV_ITEM_ACTIVE_CLASSNAME,
  Tooltip,
} from '@mineral/core';
import { FloatingDrawerPosition, UrlHistory } from './AppShell';
import { useContext } from 'react';
import { ApplicationTabsEnum } from './Enums';
import { useRBAC } from 'hooks/useRBAC';

const BaseNavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>(
  ({ className, ...restProps }, ref) => (
    <NavLink
      {...restProps}
      className={({ isActive }) =>
        [className, isActive ? NAV_ITEM_ACTIVE_CLASSNAME : null]
          .filter(Boolean)
          .join(' ')
      }
      ref={ref}
    />
  )
);

const SideNavMenuLink: React.FC<
  SideNavMenuItemProps & Omit<NavLinkProps, 'children'>
> = React.forwardRef(({ children, sx, ...restProps }, ref) => {
  return (
    <SideNavMenuItem
      ref={ref}
      component={BaseNavLink}
      {...restProps}
      sx={{
        '& .MuiListItemText-root': {
          display: 'none',
        },
        ' .MuiListItemIcon-root': {
          width: '100% !important',
        },
        my: '12px',
        ...sx,
      }}>
      {children && <Box>{children}</Box>}
    </SideNavMenuItem>
  );
});

interface ILeftNav {
  tooltip: string;
  id: ApplicationTabsEnum;
  label: string;
  to: string;
  icon: React.ReactElement;
}

export const Navigation: React.FC<{
  dockPosition?: FloatingDrawerPosition;
}> = ({ dockPosition = FloatingDrawerPosition.LEFT }) => {
  let tooltipPlacement: 'right' | 'left' | 'top' | 'bottom' = 'right';
  switch (dockPosition) {
    case FloatingDrawerPosition.LEFT:
      tooltipPlacement = 'right';
      break;
    case FloatingDrawerPosition.RIGHT:
      tooltipPlacement = 'left';
      break;
    case FloatingDrawerPosition.TOP:
      tooltipPlacement = 'bottom';
      break;
    case FloatingDrawerPosition.BOTTOM:
      tooltipPlacement = 'top';
      break;
  }
  const navRef = React.useRef<HTMLUListElement | null>(null);
  const [navWidth, setNavWidth] = React.useState<number>(40); // default width of nav is 40px
  const updateNavWidth = () => {
    const currentSideNav = navRef.current;
    if (currentSideNav) {
      if (currentSideNav.scrollHeight > currentSideNav.clientHeight) {
        setNavWidth(48);
      } else {
        setNavWidth(40);
      }
    }
  };
  const urlHistory = useContext(UrlHistory);
  const leftNavProps: ILeftNav[] = [
    {
      id: ApplicationTabsEnum.DASHBOARD,
      label: 'Home',
      to: '/',
      icon: <DashboardIcon />,
      tooltip: 'Home',
    },
    {
      id: ApplicationTabsEnum.PRODUCTS,
      label: 'Products',
      to: urlHistory.product,
      icon: <ProductsIcon />,
      tooltip: 'Products',
    },
    {
      id: ApplicationTabsEnum.CUSTOMERS,
      label: 'Customers',
      to: urlHistory.customer,
      icon: <CustomersIcon />,
      tooltip: 'Customers',
    },
    {
      id: ApplicationTabsEnum.FEATURES,
      label: 'Features',
      to: urlHistory.feature,
      icon: <FeaturesIcon />,
      tooltip: 'Features',
    },
    {
      id: ApplicationTabsEnum.PLUGINS,
      label: 'Plugins',
      to: urlHistory.plugin,
      icon: <PluginsIcon />,
      tooltip: 'Plugins',
    },
    {
      id: ApplicationTabsEnum.CONNECTORS,
      label: 'Connectors',
      to: urlHistory.connector,
      icon: <ConnectorsIcon />,
      tooltip: 'Connectors',
    },
    {
      id: ApplicationTabsEnum.INTEGRATIONS,
      label: 'Integrations',
      to: urlHistory.integration,
      icon: <IntegrationsIcon />,
      tooltip: 'Integrations',
    },
    {
      id: ApplicationTabsEnum.META_GENERATORS,
      label: 'Metadata Generator',
      to: '/metadataGenerator',
      icon: <MetaDataGeneratorIcon />,
      tooltip: 'Metadata Generator',
    },
    {
      id: ApplicationTabsEnum.REINDEX_CONTENT,
      label: 'Reindex Content',
      to: '/reindexContent',
      icon: <ContentReindexIcon />,
      tooltip: 'Reindex Content',
    },
    {
      id: ApplicationTabsEnum.LOCALIZATION_TABLE,
      label: 'Localization Table',
      to: '/localizationTable',
      icon: <Translate />,
      tooltip: 'Localization Table',
    },
    {
      id: ApplicationTabsEnum.CAMPAIGNS,
      label: 'Campaigns',
      to: urlHistory.campaign,
      icon: <Megaphone />,
      tooltip: 'Campaigns',
    },
    {
      id: ApplicationTabsEnum.ACCESS,
      label: 'Settings',
      to: '/settings',
      icon: <SettingsIcon />,
      tooltip: 'Settings',
    },
  ];

  React.useEffect(() => {
    updateNavWidth(); // do it on mount
    window.addEventListener('resize', updateNavWidth, false); // do it on resize
    return () => {
      window.removeEventListener('resize', updateNavWidth);
    };
  }, []);
  const { lookupPermissions } = useRBAC();

  const getPermittedNavs = () => {
    return leftNavProps.filter((leftNAv) =>
      lookupPermissions([leftNAv.id], 'R')
    );
  };

  return (
    <SideNavMenu
      ref={navRef}
      sx={(theme) => ({
        position: 'fixed',
        zIndex: theme.zIndex.drawer,
        display: 'flex',
        padding: '2px 4px',
        gap: [
          FloatingDrawerPosition.LEFT,
          FloatingDrawerPosition.NONE,
          FloatingDrawerPosition.RIGHT,
        ].includes(dockPosition)
          ? 0
          : theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[4],
        width: [
          FloatingDrawerPosition.LEFT,
          FloatingDrawerPosition.NONE,
          FloatingDrawerPosition.RIGHT,
        ].includes(dockPosition)
          ? navWidth
          : 'auto',
        flexDirection: [
          FloatingDrawerPosition.LEFT,
          FloatingDrawerPosition.NONE,
          FloatingDrawerPosition.RIGHT,
        ].includes(dockPosition)
          ? 'column'
          : 'row',
        right:
          dockPosition === FloatingDrawerPosition.RIGHT
            ? theme.spacing(1)
            : 'unset',
        left:
          dockPosition === FloatingDrawerPosition.LEFT ||
          dockPosition === FloatingDrawerPosition.NONE
            ? theme.spacing(1)
            : 'unset',
        maxHeight: 'calc(100vh - 64px - 64px)',
        overflowX: 'hidden',
        overflowY: 'auto',
      })}>
      {getPermittedNavs().map((navProp) => {
        const { id, tooltip, label, to, icon } = navProp;
        return (
          <Tooltip title={tooltip} placement={tooltipPlacement}>
            <SideNavMenuLink
              sx={{ marginBottom: '2px' }}
              id={id}
              label={label}
              to={to}
              icon={icon}
            />
          </Tooltip>
        );
      })}
    </SideNavMenu>
  );
};
